/* IncomeInoutList.js */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TabBar } from '../TabManager';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import ExcelImportModal from './ExcelImportModal';
import useColumnResize from '../../hooks/useColumnResize';
import useTablePagination from '../../hooks/useTablePagination';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

/* ── کمکی‌ها ── */
const toFa   = n => n===null||n===undefined?'':String(n).replace(/[0-9]/g,d=>'۰۱۲۳۴۵۶۷۸۹'[d]);
const numFmt = n => { const v=Number(n||0); return v===0?'':v.toLocaleString('fa-IR'); };
const API    = (path,opts={}) => fetch(`/api/income${path}`,{
  headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('access')}`},...opts,
}).then(jsonOf, netErr);

/* زیرکامپوننت‌ها بیرون — وگرنه فوکوس می‌پره */
const FInput = ({label,name,value,onChange,readOnly,flex=1,type='text',dir}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <input name={name} type={type} value={value??''} onChange={onChange} readOnly={readOnly}
      style={{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',
        fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',
        direction:dir||'rtl',textAlign:dir==='ltr'?'left':'right',
        width:'100%',boxSizing:'border-box',background:readOnly?'#f5f5f5':'#fff'}}/>
  </div>
);

/* شناسه واریز — ۱۵ رقم آخر */
const ShenaseCell = ({value}) => {
  const v = String(value||'');
  if (v.length <= 15) return <span>{toFa(v)}</span>;
  return (<span style={{direction:'ltr',display:'inline-block'}}>
    <span>{toFa(v.slice(0,v.length-15))}</span>
    <span style={{color:'#1565c0',fontWeight:'700'}}>{toFa(v.slice(v.length-15))}</span>
  </span>);
};

const RADIO_MODES = [
  {val:'all',      label:'همه اطلاعات'},
  {val:'known',    label:'فقط مبالغ وصولی شناسایی شده'},
  {val:'unknown',  label:'فقط مبالغ وصولی شناسایی نشده'},
  {val:'withdraw', label:'فقط مبالغ برداشتی'},
];

/* ستون‌های گرید اصلی */
const INIT_WIDTHS = [45,65,110,100,70,185,120,120,120,110,110,110,155,155,200,70,100,70,95];
const EMPTY = {serial_no:0,transaction_no:'',transaction_date:'',transaction_time:'',
  shenase:'',tracking_code:'',amount:0,remark:'',sent:0};
const EMPTY_SAMP = {samp_shenase:'',department_id:0,department:'',income_id:0,income_title:'',
  samp_date:'',samp_amount:0,file_no:''};

export default function IncomeInoutList({ user, onBack }) {
  // دکمه‌های فرم FRMINCOMEINOUT — دیدن = دسترسی
  const vbf = useFormButtons('FRMINCOMEINOUT', ["BTNNEW","BTNEDIT","BTNINCOMESAMP","BTNDELETE","BTNGETINCOMEFROMBANKRESUALT","BTNGETSAMP","BTNSHOW","BTNOK"]);
  // دکمه‌های فرم FRMINCOMESAMP — دیدن = دسترسی
  const vbf_incomesamp = useFormButtons('FRMINCOMESAMP', ["BTNNEW","BTNEDIT","BTNDELETE"]);
  /* ── گرید اصلی ── */
  const [rows,setRows]         = useState([]);
  const [total,setTotal]       = useState(0);
  const [sumAmount,setSumAmount]= useState(0);
  const [hasMore,setHasMore]   = useState(false);
  const [loading,setLoading]   = useState(false);
  const [selected,setSelected] = useState(null);

  /* ── فیلترها ── */
  const [fromDate,setFromDate] = useState('');
  const [toDate,setToDate]     = useState('');
  const [mode,setMode]         = useState('all');
  const [ready,setReady]       = useState(false);

  /* ── موجودی حساب تا تاریخ ── */
  const [remainDate,setRemainDate] = useState('');
  const [remain,setRemain]         = useState(null);

  /* ── مودال‌ها ── */
  const [modal,setModal]   = useState(null);      // new | edit | delete | samp
  const [form,setForm]     = useState(EMPTY);
  const [error,setError]   = useState('');
  const [warns,setWarns]   = useState([]);
  const [toast,setToast]   = useState('');

  /* ── زیرگریدها ── */
  const [subRefunds,setSubRefunds] = useState([]);
  const [subSamps,setSubSamps]     = useState([]);
  const [sumSamp,setSumSamp]       = useState(0);

  /* ── مودال سمپ ── */
  const [sampForm,setSampForm]   = useState(EMPTY_SAMP);
  const [sampEditId,setSampEditId]= useState(null);
  const [sampError,setSampError] = useState('');
  const [lookup,setLookup]       = useState({kind:null,rows:[]});

  /* ── ورود اکسل: 'bank' | 'samp' ── */
  const [importKind,setImportKind] = useState(null);

  const offsetRef = useRef(0);

  const {TH}   = useColumnResize(INIT_WIDTHS);
  const {visibleRows,search,setSearch,totalCount,LoadMoreButton} = useTablePagination({
    allRows:rows, searchFields:['shenase','tracking_code','transaction_no','remark'],
    serverTotal:total, serverHasMore:hasMore,
  });

  /* مقداردهی اولیه + بازه پیش‌فرض */
  useEffect(()=>{ (async()=>{
    try{
      const d = await API('/inout/init/');
      if(d.ok){
        setFromDate(d.from_date); setToDate(d.to_date); setRemainDate(d.to_date);
        if(d.duplicates_removed>0) showToast(`🧹 ${toFa(d.duplicates_removed)} رکورد تکراری حذف شد`);
      }
    }catch(e){console.error(e);}
    setReady(true);
  })(); },[]);

  const fetchData = useCallback(async(reset=false)=>{
    if(!fromDate||!toDate) return;
    if(reset){ offsetRef.current=0; setRows([]); setSelected(null); }
    setLoading(true);
    try{
      const off = reset?0:offsetRef.current;
      const d = await API(`/inout/?offset=${off}&from_date=${fromDate}&to_date=${toDate}&mode=${mode}`);
      if(d.ok){
        setRows(p=>reset?d.rows:[...p,...d.rows]);
        setTotal(d.total); setSumAmount(d.sum_amount); setHasMore(d.has_more);
        offsetRef.current = (reset?0:offsetRef.current)+d.rows.length;
      }
    }catch(e){console.error(e);}
    setLoading(false);
  },[fromDate,toDate,mode]);

  /* تغییر رادیوباتن → بازخوانی */
  useEffect(()=>{ if(ready&&fromDate&&toDate) fetchData(true); },[mode,ready]); // eslint-disable-line

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),2800); };

  /* انتخاب ردیف → بارگذاری زیرگریدها */
  const selectRow = async r => {
    setSelected(r); setSubRefunds([]); setSubSamps([]); setSumSamp(0);
    if(r.refund_sum!==0){
      const d = await API(`/inout/${r.id}/refunds/`);
      if(d.ok) setSubRefunds(d.rows);
    }
    if(r.samp_amount!==0 && r.sub_samp){
      const d = await API(`/samps/?sub_samp=${encodeURIComponent(r.sub_samp)}`);
      if(d.ok){ setSubSamps(d.rows); setSumSamp(d.sum_samp); }
    }
  };
  const reloadSamps = async()=>{
    if(!selected?.sub_samp) return;
    const d = await API(`/samps/?sub_samp=${encodeURIComponent(selected.sub_samp)}`);
    if(d.ok){ setSubSamps(d.rows); setSumSamp(d.sum_samp); }
  };

  const handleChange = e => { const{name,value}=e.target; setForm(p=>({...p,[name]:value})); };

  /* ── جدید / ویرایش ── */
  const openNew  = ()=>{ if (!vbf.can('BTNNEW')) return; setForm(EMPTY); setError(''); setWarns([]); setModal('new'); };
  const openEdit = ()=>{ if (!vbf.can('BTNEDIT')) return;
    if(!selected) return;
    if(selected.doc_detail_id){ alert('برای این درآمد سند حسابداری ثبت شده و قابل ویرایش نیست'); return; }
    setForm({serial_no:selected.serial_no,transaction_no:selected.transaction_no,
      transaction_date:selected.transaction_date,transaction_time:selected.transaction_time,
      shenase:selected.shenase,tracking_code:selected.tracking_code,amount:selected.amount,
      remark:selected.remark,sent:selected.sent});
    setError(''); setWarns([]); setModal('edit');
  };

  /* اعتبارسنجی سمت کلاینت */
  const validate = f => {
    if(!String(f.shenase||'').trim())          return 'شناسه مشخص نشده است';
    if(!String(f.transaction_no||'').trim())   return 'شماره تراکنش مشخص نشده است';
    if(String(f.transaction_date||'').length!==10) return 'تاریخ تراکنش معتبر نیست';
    if(!String(f.tracking_code||'').trim())    return 'کد پیگیری مشخص نشده است';
    if(Number(f.amount||0)===0)                return 'مبلغ درآمد مشخص نشده است';
    return null;
  };

  const handleSave = async()=>{
    const v = validate(form);
    if(v){ setError(v); return; }
    setError(''); setWarns([]);
    const url = modal==='new' ? '/inout/create/' : `/inout/${selected.id}/update/`;
    const d = await API(url,{method:'POST',body:JSON.stringify(form)});
    if(d.ok){
      if(d.warnings?.length){ setWarns(d.warnings); showToast('⚠️ ثبت شد — هشدار تکراری'); }
      else showToast(modal==='new'?'✅ ثبت شد':'✅ ویرایش شد');
      setModal(null); fetchData(true);
    } else setError(d.error||'خطا');
  };

  const handleDelete = async()=>{
    if(!selected) return;
    const d = await API(`/inout/${selected.id}/delete/`,{method:'POST'});
    if(d.ok){ setModal(null); setSelected(null); fetchData(true); showToast('✅ حذف شد'); }
    else setError(d.error||'خطا');
  };

  /* موجودی حساب تا تاریخ */
  const fetchRemain = async d0 => {
    const dt = d0||remainDate;
    if(String(dt||'').length!==10) return;
    const d = await API(`/inout/remain/?to_date=${dt}`);
    if(d.ok) setRemain(d.remain);
  };

  /* ── ریز اطلاعات سمپ ── */
  const openSampModal = ()=>{ if (!vbf.can('BTNINCOMESAMP')) return;
    if(!selected){ return; }
    if(Number(selected.amount)<=0){ alert('فقط برای مبالغ واریزی ثبت مشخصات سمپ امکانپذیر است'); return; }
    setSampForm({...EMPTY_SAMP, samp_shenase:selected.sub_samp});
    setSampEditId(null); setSampError(''); setLookup({kind:null,rows:[]}); setModal('samp');
  };
  const editSampRow = r => {
    setSampForm({samp_shenase:r.samp_shenase,department_id:r.department_id,department:r.department,
      income_id:r.income_id,income_title:r.income_title,samp_date:r.samp_date,
      samp_amount:r.samp_amount,file_no:r.file_no});
    setSampEditId(r.id); setSampError('');
  };
  const doLookup = async(kind,q)=>{
    const d = await API(`/lookup/?kind=${kind}&q=${encodeURIComponent(q||'')}`);
    if(d.ok) setLookup({kind,rows:d.rows});
  };
  const pickLookup = row => {
    if(lookup.kind==='department') setSampForm(p=>({...p,department_id:row.id,department:row.title}));
    else setSampForm(p=>({...p,income_id:row.id,income_title:row.title}));
    setLookup({kind:null,rows:[]});
  };
  const saveSamp = async()=>{ if (!(vbf_incomesamp.can('BTNNEW') || vbf_incomesamp.can('BTNEDIT'))) return;
    const f = sampForm, sub = selected?.sub_samp||'';
    if(!Number(f.department_id)) { setSampError('واحد اجرایی مشخص نیست'); return; }
    if(Number(f.samp_amount)<=0) { setSampError('مبلغ سمپ مشخص نیست'); return; }
    if(!Number(f.income_id))     { setSampError('نوع درآمد مشخص نیست'); return; }
    const smp = String(f.samp_shenase||'');
    if(smp.length<sub.length || (sub && smp.slice(smp.length-sub.length)!==sub)){
      setSampError('شناسه سمپ اشتباه است'); return;
    }
    setSampError('');
    const url = sampEditId ? `/samps/${sampEditId}/update/` : '/samps/create/';
    const d = await API(url,{method:'POST',body:JSON.stringify({...f,sub_samp:sub})});
    if(d.ok){ setSampForm({...EMPTY_SAMP,samp_shenase:sub}); setSampEditId(null);
      await reloadSamps(); fetchData(true); showToast('✅ ذخیره شد'); }
    else setSampError(d.error||'خطا');
  };
  const deleteSamp = async id => {
    if(!window.confirm('آیا از حذف این ردیف سمپ مطمئن هستید؟')) return;
    const d = await API(`/samps/${id}/delete/`,{method:'POST'});
    if(d.ok){ await reloadSamps(); fetchData(true); showToast('✅ حذف شد'); }
    else alert(d.error||'خطا');
  };

  const sampRemain = (Number(selected?.amount)||0) - sumSamp;

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.hLeft}>
          <button onClick={onBack} style={s.backBtn}>← بازگشت</button>
          <div style={s.hLogo}><img src={require('../../assets/images/arjan.png')} alt="" style={{width:'34px',height:'34px',borderRadius:'7px'}}/></div>
          <div><div style={s.hTitle}>وصول درآمد</div><div style={s.hSub}>درآمدها به تفکیک واحدهای اجرایی — بخش درآمد</div></div>
        </div>
        <span style={s.userName}>{user?.full_name}</span>
      </header>
      <TabBar/>

      <main style={s.main}>
        {/* ── نوار ابزار ── */}
        <div style={s.toolbar}>
          <div style={s.toolBtns}>
            {vbf.can('BTNNEW') && <button onClick={openNew} style={s.btnNew}>＋ جدید</button>}
            {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{...s.btnEdit,opacity:selected?1:.4}}>✎ ویرایش</button>}
            {vbf.can('BTNDELETE') && <button onClick={()=>{if(selected){setError('');setModal('delete');}}} disabled={!selected}
              style={{...s.btnDel,opacity:selected?1:.4}}>✕ حذف</button>}
            {vbf.can('BTNINCOMESAMP') && <button onClick={openSampModal} disabled={!selected}
              style={{...s.btnSamp,opacity:selected?1:.4}}>📄 ریز اطلاعات سمپ</button>}
            {vbf.can('BTNGETINCOMEFROMBANKRESUALT') && <button onClick={()=>setImportKind('bank')} style={s.btnBank}>🏦 دریافت از بانک</button>}
            {vbf.can('BTNGETSAMP') && <button onClick={()=>setImportKind('samp')} style={s.btnGetSamp}>💠 دریافت سمپ</button>}
          </div>
          <div style={{display:'flex',alignItems:'flex-end',gap:'8px',marginRight:'auto'}}>
            <div><div style={s.fL}>از تاریخ</div><JalaliDatePicker value={fromDate} onChange={setFromDate} placeholder="از تاریخ"/></div>
            <div><div style={s.fL}>تا</div><JalaliDatePicker value={toDate} onChange={setToDate} placeholder="تا تاریخ"/></div>
            {vbf.can('BTNSHOW') && <button onClick={()=>fetchData(true)} style={s.btnShow}>🔍 نمایش اطلاعات</button>}
          </div>
        </div>

        {/* ── فیلتر وضعیت + جستجو + موجودی ── */}
        <div style={s.toolbar2}>
          <div style={{display:'flex',gap:'14px',flexWrap:'wrap'}}>
            {RADIO_MODES.map(m=>(
              <label key={m.val} style={s.radio}>
                <input type="radio" name="mode" checked={mode===m.val} onChange={()=>setMode(m.val)}/>
                <span>{m.label}</span>
              </label>
            ))}
          </div>
          <div style={{marginRight:'auto',display:'flex',alignItems:'flex-end',gap:'8px'}}>
            <div><div style={s.fL}>جستجو</div>
              <input value={search} onChange={e=>setSearch(e.target.value)}
                placeholder="شناسه / کد پیگیری / شماره تراکنش / شرح" style={{...s.fI,width:'250px'}}/></div>
            <div><div style={s.fL}>موجودی حساب تا تاریخ</div>
              <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
                <JalaliDatePicker value={remainDate} onChange={v=>{setRemainDate(v);fetchRemain(v);}} placeholder="تاریخ"/>
                <span style={s.remainBox}>{remain===null?'—':numFmt(remain)||'۰'}</span>
              </div></div>
          </div>
        </div>

        {/* ── گرید اصلی ── */}
        <div style={s.tableWrap}>
          <table style={{width:'100%',borderCollapse:'collapse',tableLayout:'fixed'}}>
            <thead style={{position:'sticky',top:0,zIndex:3}}>
              <tr>
                <TH idx={0}>ردیف</TH>
                <TH idx={1}>سریال</TH>
                <TH idx={2}>شماره تراکنش</TH>
                <TH idx={3}>تاریخ تراکنش</TH>
                <TH idx={4}>زمان</TH>
                <TH idx={5}>شناسه واریز</TH>
                <TH idx={6}>کد پیگیری</TH>
                <TH idx={7} gold>مبلغ واریزی</TH>
                <TH idx={8} gold>جمع مبلغ سمپ</TH>
                <TH idx={9} gold>مغایرت</TH>
                <TH idx={10} red>جمع استرداد</TH>
                <TH idx={11} red>مانده</TH>
                <TH idx={12} right>واحد اجرایی</TH>
                <TH idx={13} right>نوع درآمد</TH>
                <TH idx={14} right>شرح</TH>
                <TH idx={15}>سند</TH>
                <TH idx={16}>تاریخ سند</TH>
                <TH idx={17}>ردیف سند</TH>
                <TH idx={18}>وضعیت</TH>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length===0 && (
                <tr><td colSpan={19} style={s.empty}>
                  {loading?'در حال بارگذاری...':'اطلاعاتی برای نمایش وجود ندارد — بازه تاریخ را انتخاب و «نمایش اطلاعات» را بزنید'}
                </td></tr>)}
              {visibleRows.map((r,idx)=>{
                const sel = selected?.id===r.id;
                const bg  = sel ? '#64b5f6'
                          : r.amount<0 ? '#ffebee'
                          : r.diff!==0 ? '#fff8e1'
                          : idx%2===0 ? '#fff' : '#fdf6e3';
                return (
                  <tr key={r.id} onClick={()=>selectRow(r)} style={{cursor:'pointer',background:bg}}
                    onMouseEnter={e=>{if(!sel)e.currentTarget.style.background='#bbdefb';}}
                    onMouseLeave={e=>{if(!sel)e.currentTarget.style.background=bg;}}>
                    <td style={s.td}>{toFa(idx+1)}</td>
                    <td style={s.td}>{toFa(r.serial_no)}</td>
                    <td style={s.td}>{toFa(r.transaction_no)}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.transaction_date)}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.transaction_time)}</td>
                    <td style={{...s.td,fontSize:'11px'}}><ShenaseCell value={r.shenase}/></td>
                    <td style={{...s.td,direction:'ltr',fontSize:'11px'}}>{toFa(r.tracking_code)}</td>
                    <td style={{...s.td,...s.num,color:r.amount<0?'#b71c1c':'#1b5e20'}}>{numFmt(r.amount)}</td>
                    <td style={{...s.td,...s.num,color:'#1565c0'}}>{numFmt(r.samp_amount)}</td>
                    <td style={{...s.td,...s.num,color:r.diff!==0?'#e65100':'#999'}}>{numFmt(r.diff)}</td>
                    <td style={{...s.td,...s.num,color:'#6a1b9a'}}>{numFmt(r.refund_sum)}</td>
                    <td style={{...s.td,...s.num}}>{r.refund_sum!==0?numFmt(r.remain):''}</td>
                    <td style={{...s.td,textAlign:'right'}}>{r.department}</td>
                    <td style={{...s.td,textAlign:'right'}}>{r.income_title}</td>
                    <td style={{...s.td,textAlign:'right'}} title={r.remark}>{r.remark}</td>
                    <td style={s.td}>{r.document_no?toFa(r.document_no):''}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.document_date)}</td>
                    <td style={s.td}>{r.paper_no?toFa(r.paper_no):''}</td>
                    <td style={s.td}>{r.sent
                      ? <span style={{color:'#1b5e20',fontWeight:'700'}}>✅ ارسال شده</span>
                      : <span style={{color:'#888'}}>—</span>}</td>
                  </tr>);
              })}
            </tbody>
          </table>
        </div>
        <LoadMoreButton onClick={()=>fetchData(false)} loading={loading}/>

        {/* ── نوار جمع‌ها ── */}
        <div style={s.summary}>
          <span>تعداد: <b>{toFa(visibleRows.length)}</b> از <b>{toFa(totalCount)}</b></span>
          <span>جمع مبلغ بازه: <b style={{color:'#1b5e20'}}>{numFmt(sumAmount)||'۰'}</b> ریال</span>
          {selected && <>
            <span>مبلغ واریزی ردیف: <b>{numFmt(selected.amount)}</b></span>
            <span>جمع سمپ: <b style={{color:'#1565c0'}}>{numFmt(selected.samp_amount)}</b></span>
            <span>مغایرت: <b style={{color:selected.diff!==0?'#e65100':'#1b5e20'}}>{numFmt(selected.diff)||'۰'}</b></span>
          </>}
        </div>

        {/* ── زیرگرید استرداد ── */}
        {subRefunds.length>0 && (
          <div style={s.subWrap}>
            <div style={{...s.subTitle,background:'linear-gradient(135deg,#4a148c,#6a1b9a)'}}>💜 استردادهای این واریزی</div>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>
                {['شماره','سال','شماره درخواست','تاریخ درخواست','درخواست کننده','مبلغ استرداد','دریافت کننده','کد ملی','حساب','خطای حساب شبا','وضعیت']
                  .map(h=><th key={h} style={{...s.subTh,background:'#4a148c'}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {subRefunds.map((r,i)=>(
                  <tr key={r.id} style={{background:i%2===0?'#fff':'#f3e5f5'}}>
                    <td style={s.subTd}>{toFa(r.refund_no)}</td>
                    <td style={s.subTd}>{toFa(r.refund_year)}</td>
                    <td style={s.subTd}>{toFa(r.request_no)}</td>
                    <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.request_date)}</td>
                    <td style={{...s.subTd,textAlign:'right'}}>{r.order_person}</td>
                    <td style={{...s.subTd,...s.num,color:'#6a1b9a'}}>{numFmt(r.refund_pay)}</td>
                    <td style={{...s.subTd,textAlign:'right'}}>{r.taker}</td>
                    <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.melli_code)}</td>
                    <td style={{...s.subTd,direction:'ltr',fontSize:'11px'}}>{r.account_no}</td>
                    <td style={{...s.subTd,color:'#c62828',fontWeight:'700'}}>{r.sheba_error}</td>
                    <td style={s.subTd}>{r.sent?'✅':'—'}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── زیرگرید سمپ ── */}
        {subSamps.length>0 && (
          <div style={s.subWrap}>
            <div style={{...s.subTitle,background:'linear-gradient(135deg,#006064,#00838f)'}}>
              💠 ریز سمپ — جمع: {numFmt(sumSamp)} ریال | مانده شناسایی نشده: <b>{numFmt(sampRemain)||'۰'}</b>
            </div>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>
                {['شناسه سمپ','واحد اجرایی','شهر','نوع درآمد','تاریخ سمپ','مبلغ سمپ','شماره پرونده','دستی']
                  .map(h=><th key={h} style={{...s.subTh,background:'#006064'}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {subSamps.map((r,i)=>(
                  <tr key={r.id} style={{background:i%2===0?'#fff':'#e0f7fa'}}>
                    <td style={{...s.subTd,direction:'ltr',fontSize:'11px'}}>{toFa(r.samp_shenase)}</td>
                    <td style={{...s.subTd,textAlign:'right'}}>{r.department}</td>
                    <td style={s.subTd}>{r.city}</td>
                    <td style={{...s.subTd,textAlign:'right'}}>{r.income_title}</td>
                    <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.samp_date)}</td>
                    <td style={{...s.subTd,...s.num,color:'#006064'}}>{numFmt(r.samp_amount)}</td>
                    <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.file_no)}</td>
                    <td style={s.subTd}>{r.is_manual?'✍️':'—'}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* ══ مودال جدید / ویرایش ══ */}
      {(modal==='new'||modal==='edit') && (
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.mHeader}>
              <div style={s.mTitle}>{modal==='new'?'➕ ثبت درآمد جدید':'✎ ویرایش درآمد'}</div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button>
            </div>
            <div style={s.mBody}>
              {error&&<div style={s.errBox}>{error}</div>}
              {warns.map((w,i)=><div key={i} style={s.warnBox}>⚠️ {w}</div>)}
              <div style={s.row}>
                <FInput label="سریال" name="serial_no" value={form.serial_no} onChange={handleChange} type="number" flex="0 0 22%"/>
                <FInput label="شماره تراکنش *" name="transaction_no" value={form.transaction_no} onChange={handleChange} flex="0 0 36%"/>
                <FInput label="کد پیگیری *" name="tracking_code" value={form.tracking_code} onChange={handleChange} dir="ltr" flex="0 0 36%"/>
              </div>
              <div style={s.row}>
                <div style={{flex:'0 0 36%',display:'flex',flexDirection:'column',gap:'4px'}}>
                  <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>تاریخ تراکنش *</label>
                  <JalaliDatePicker value={form.transaction_date}
                    onChange={v=>setForm(p=>({...p,transaction_date:v}))} placeholder="تاریخ تراکنش"/>
                </div>
                <FInput label="زمان تراکنش" name="transaction_time" value={form.transaction_time} onChange={handleChange} dir="ltr" flex="0 0 24%"/>
                <FInput label="مبلغ واریزی *" name="amount" value={form.amount} onChange={handleChange} type="number" dir="ltr" flex="0 0 34%"/>
              </div>
              <div style={s.row}>
                <FInput label="شناسه واریز *" name="shenase" value={form.shenase} onChange={handleChange} dir="ltr"/>
              </div>
              <div style={s.row}>
                <FInput label="شرح" name="remark" value={form.remark} onChange={handleChange}/>
              </div>
              <label style={{display:'flex',alignItems:'center',gap:'8px',fontSize:'13px',fontWeight:'600',color:'#555'}}>
                <input type="checkbox" checked={!!Number(form.sent)}
                  onChange={e=>setForm(p=>({...p,sent:e.target.checked?1:0}))}/>
                ارسال به تنظیم حساب
              </label>
              {modal==='edit'&&selected?.document_no>0&&(
                <div style={s.docBox}>
                  سند حسابداری: شماره <b>{toFa(selected.document_no)}</b> — تاریخ <b>{toFa(selected.document_date)}</b> — ردیف <b>{toFa(selected.paper_no)}</b>
                </div>)}
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>setModal(null)} style={s.btnCancel}>لغو</button>
              {vbf.can('BTNOK') && <button onClick={handleSave} style={s.btnSave}>ثبت</button>}
            </div>
          </div>
        </div>
      )}

      {/* ══ مودال حذف ══ */}
      {modal==='delete'&&selected&&(
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={{...s.modal,maxWidth:'460px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:'linear-gradient(135deg,#b71c1c,#c62828)'}}>
              <div style={s.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button></div>
            <div style={s.mBody}>
              {error&&<div style={s.errBox}>{error}</div>}
              <p style={{fontSize:'14px',lineHeight:'2'}}>
                آیا از حذف تراکنش شماره <b>{toFa(selected.transaction_no)}</b> به مبلغ <b>{numFmt(selected.amount)}</b> ریال مطمئن هستید؟</p>
              {selected.doc_detail_id>0&&<p style={{color:'#c62828',fontWeight:'700'}}>
                ⚠️ برای این درآمد سند حسابداری ثبت شده و قابل حذف نیست.</p>}
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>setModal(null)} style={s.btnCancel}>لغو</button>
              {!selected.doc_detail_id&&(vbf.can('BTNDELETE') && <button onClick={handleDelete}
                style={{...s.btnSave,background:'linear-gradient(135deg,#b71c1c,#c62828)'}}>حذف</button>)}
            </div>
          </div>
        </div>
      )}

      {/* ══ مودال ریز اطلاعات سمپ ══ */}
      {modal==='samp'&&selected&&(
        <div style={s.overlay} onClick={()=>{setModal(null);setLookup({kind:null,rows:[]});}}>
          <div style={{...s.modal,maxWidth:'980px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:'linear-gradient(135deg,#006064,#00838f)'}}>
              <div style={s.mTitle}>📄 ریز اطلاعات سمپ</div>
              <button onClick={()=>{setModal(null);setLookup({kind:null,rows:[]});}} style={s.closeBtn}>✕</button></div>
            <div style={s.mBody}>
              <div style={s.sampInfo}>
                <span>مبلغ واریزی: <b>{numFmt(selected.amount)}</b></span>
                <span>جمع سمپ: <b style={{color:'#006064'}}>{numFmt(sumSamp)||'۰'}</b></span>
                <span>مانده: <b style={{color:sampRemain!==0?'#e65100':'#1b5e20'}}>{numFmt(sampRemain)||'۰'}</b></span>
                <span>شناسه پایه: <b style={{direction:'ltr'}}>{toFa(selected.sub_samp)}</b></span>
              </div>
              {sampError&&<div style={s.errBox}>{sampError}</div>}
              <div style={s.row}>
                <FInput label="شناسه سمپ *" name="samp_shenase" value={sampForm.samp_shenase}
                  onChange={e=>setSampForm(p=>({...p,samp_shenase:e.target.value}))} dir="ltr" flex="0 0 32%"/>
                <div style={{flex:'0 0 32%',display:'flex',flexDirection:'column',gap:'4px',position:'relative'}}>
                  <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>واحد اجرایی *</label>
                  <input value={sampForm.department} placeholder="تایپ کنید..."
                    onChange={e=>{setSampForm(p=>({...p,department:e.target.value,department_id:0}));doLookup('department',e.target.value);}}
                    style={s.fI}/>
                  {lookup.kind==='department'&&lookup.rows.length>0&&(
                    <div style={s.dropdown}>{lookup.rows.map(r=>(
                      <div key={r.id} onClick={()=>pickLookup(r)} style={s.dropItem}>{r.code} — {r.title}</div>))}</div>)}
                </div>
                <div style={{flex:'0 0 32%',display:'flex',flexDirection:'column',gap:'4px',position:'relative'}}>
                  <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>نوع درآمد *</label>
                  <input value={sampForm.income_title} placeholder="تایپ کنید..."
                    onChange={e=>{setSampForm(p=>({...p,income_title:e.target.value,income_id:0}));doLookup('income',e.target.value);}}
                    style={s.fI}/>
                  {lookup.kind==='income'&&lookup.rows.length>0&&(
                    <div style={s.dropdown}>{lookup.rows.map(r=>(
                      <div key={r.id} onClick={()=>pickLookup(r)} style={s.dropItem}>{r.code} — {r.title}</div>))}</div>)}
                </div>
              </div>
              <div style={s.row}>
                <div style={{flex:'0 0 32%',display:'flex',flexDirection:'column',gap:'4px'}}>
                  <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>تاریخ سمپ</label>
                  <JalaliDatePicker value={sampForm.samp_date}
                    onChange={v=>setSampForm(p=>({...p,samp_date:v}))} placeholder="تاریخ سمپ"/>
                </div>
                <FInput label="مبلغ سمپ *" name="samp_amount" value={sampForm.samp_amount}
                  onChange={e=>setSampForm(p=>({...p,samp_amount:e.target.value}))} type="number" dir="ltr" flex="0 0 32%"/>
                <FInput label="شماره پرونده" name="file_no" value={sampForm.file_no}
                  onChange={e=>setSampForm(p=>({...p,file_no:e.target.value}))} dir="ltr" flex="0 0 32%"/>
              </div>
              <div style={{display:'flex',gap:'8px',marginBottom:'14px'}}>
                {(vbf_incomesamp.can('BTNNEW') || vbf_incomesamp.can('BTNEDIT')) && <button onClick={saveSamp} style={{...s.btnSave,background:'linear-gradient(135deg,#006064,#00838f)'}}>
                  {sampEditId?'ذخیره ویرایش':'افزودن ردیف'}</button>}
                {sampEditId&&<button onClick={()=>{setSampEditId(null);setSampForm({...EMPTY_SAMP,samp_shenase:selected.sub_samp});}}
                  style={s.btnCancel}>انصراف از ویرایش</button>}
              </div>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr>
                  {['شناسه سمپ','واحد اجرایی','نوع درآمد','تاریخ','مبلغ','پرونده','عملیات']
                    .map(h=><th key={h} style={{...s.subTh,background:'#006064'}}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {subSamps.length===0&&<tr><td colSpan={7} style={s.empty}>ردیفی ثبت نشده است</td></tr>}
                  {subSamps.map((r,i)=>(
                    <tr key={r.id} style={{background:i%2===0?'#fff':'#e0f7fa'}}>
                      <td style={{...s.subTd,direction:'ltr',fontSize:'11px'}}>{toFa(r.samp_shenase)}</td>
                      <td style={{...s.subTd,textAlign:'right'}}>{r.department}</td>
                      <td style={{...s.subTd,textAlign:'right'}}>{r.income_title}</td>
                      <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.samp_date)}</td>
                      <td style={{...s.subTd,...s.num}}>{numFmt(r.samp_amount)}</td>
                      <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.file_no)}</td>
                      <td style={s.subTd}>
                        {vbf_incomesamp.can('BTNEDIT') && <button onClick={()=>editSampRow(r)} style={s.miniBtn}>✎</button>}
                        {vbf_incomesamp.can('BTNDELETE') && <button onClick={()=>deleteSamp(r.id)} style={{...s.miniBtn,background:'#c62828'}}>✕</button>}
                      </td>
                    </tr>))}
                </tbody>
              </table>
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>{setModal(null);setLookup({kind:null,rows:[]});}} style={s.btnCancel}>بستن</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ مودال دریافت از اکسل ══ */}
      {importKind&&(
        <ExcelImportModal kind={importKind}
          onClose={()=>setImportKind(null)}
          onDone={()=>{fetchData(true);showToast('✅ اطلاعات دریافت شد');}}/>
      )}

      {toast&&<div style={s.toast}>{toast}</div>}
    </div>
  );
}

const s = {
  page:{height:'100vh',overflow:'hidden',background:'#f0f2f7',fontFamily:"'Vazirmatn',Tahoma,sans-serif",direction:'rtl',display:'flex',flexDirection:'column'},
  header:{background:'linear-gradient(135deg,#0a1628,#1a2f50)',padding:'10px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',boxShadow:'0 2px 16px rgba(0,0,0,0.3)',flexShrink:0},
  hLeft:{display:'flex',alignItems:'center',gap:'12px'},
  backBtn:{padding:'6px 14px',background:'rgba(255,255,255,0.12)',color:'#fff',border:'1px solid rgba(255,255,255,0.25)',borderRadius:'7px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  hLogo:{width:'40px',height:'40px',background:'rgba(200,168,75,0.12)',border:'1px solid rgba(200,168,75,0.3)',borderRadius:'9px',display:'flex',alignItems:'center',justifyContent:'center'},
  hTitle:{fontSize:'15px',fontWeight:'700',color:'#fff'},
  hSub:{fontSize:'11px',color:'rgba(255,255,255,0.55)'},
  userName:{fontSize:'13px',color:'rgba(255,255,255,0.85)'},
  main:{padding:'8px 20px',flex:1,display:'flex',flexDirection:'column',overflow:'auto',minHeight:0},
  toolbar:{display:'flex',alignItems:'center',gap:'8px',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',marginBottom:'6px',flexWrap:'wrap',flexShrink:0},
  toolbar2:{display:'flex',alignItems:'center',gap:'8px',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',marginBottom:'6px',flexWrap:'wrap',flexShrink:0},
  toolBtns:{display:'flex',gap:'7px'},
  radio:{display:'flex',alignItems:'center',gap:'5px',fontSize:'12px',fontWeight:'600',color:'#444',cursor:'pointer'},
  fL:{fontSize:'11px',fontWeight:'600',color:'#555',marginBottom:'3px'},
  fI:{padding:'7px 11px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',textAlign:'right',boxSizing:'border-box',width:'100%'},
  remainBox:{padding:'7px 12px',background:'#1a2f50',color:'#C8A84B',borderRadius:'8px',fontSize:'12px',fontWeight:'700',direction:'ltr',minWidth:'110px',textAlign:'center'},
  btnNew:{padding:'7px 16px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  btnEdit:{padding:'7px 16px',background:'linear-gradient(135deg,#1a237e,#283593)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnDel:{padding:'7px 16px',background:'linear-gradient(135deg,#b71c1c,#c62828)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnSamp:{padding:'7px 16px',background:'linear-gradient(135deg,#006064,#00838f)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnBank:{padding:'7px 16px',background:'linear-gradient(135deg,#e65100,#f57c00)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  btnGetSamp:{padding:'7px 16px',background:'linear-gradient(135deg,#4a148c,#6a1b9a)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  btnShow:{padding:'7px 16px',background:'linear-gradient(135deg,#37474f,#455a64)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  tableWrap:{overflowX:'auto',overflowY:'auto',borderRadius:'10px',boxShadow:'0 2px 8px rgba(0,0,0,0.1)',background:'#fff',flex:1,minHeight:'260px'},
  td:{padding:'6px 8px',borderBottom:'1px solid #f0f0f0',textAlign:'center',fontSize:'12px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'},
  num:{direction:'ltr',textAlign:'left',fontWeight:'700'},
  empty:{textAlign:'center',padding:'40px',color:'#999',fontSize:'13px'},
  summary:{display:'flex',gap:'20px',flexWrap:'wrap',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',margin:'6px 0',fontSize:'12px',color:'#444',flexShrink:0},
  subWrap:{background:'#fff',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.1)',marginBottom:'8px',overflow:'hidden',flexShrink:0},
  subTitle:{padding:'7px 14px',color:'#fff',fontSize:'12px',fontWeight:'700'},
  subTh:{padding:'7px 9px',color:'#fff',fontSize:'11px',fontWeight:'700',whiteSpace:'nowrap',position:'sticky',top:0,zIndex:2},
  subTd:{padding:'6px 8px',borderBottom:'1px solid #f0f0f0',textAlign:'center',fontSize:'12px',whiteSpace:'nowrap'},
  miniBtn:{padding:'3px 8px',margin:'0 2px',background:'#1a237e',color:'#fff',border:'none',borderRadius:'5px',cursor:'pointer',fontSize:'11px'},
  overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'},
  modal:{background:'#fff',borderRadius:'16px',width:'100%',maxWidth:'720px',maxHeight:'92vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.4)'},
  mHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 20px',background:'linear-gradient(135deg,#0a1628,#1a2f50)',borderRadius:'16px 16px 0 0'},
  mTitle:{fontSize:'15px',fontWeight:'700',color:'#fff'},
  closeBtn:{background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',color:'#fff',width:'30px',height:'30px',borderRadius:'50%',cursor:'pointer',fontSize:'13px',display:'flex',alignItems:'center',justifyContent:'center'},
  mBody:{overflowY:'auto',flex:1,padding:'18px'},
  mFooter:{display:'flex',justifyContent:'flex-end',gap:'10px',padding:'12px 18px',borderTop:'1px solid #f0f2f7',background:'#f8f9fa',borderRadius:'0 0 16px 16px'},
  row:{display:'flex',gap:'12px',marginBottom:'12px',flexWrap:'wrap'},
  errBox:{background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'8px',padding:'10px 14px',color:'#c62828',marginBottom:'10px',fontSize:'13px'},
  warnBox:{background:'#fff8e1',border:'1px solid #ffe082',borderRadius:'8px',padding:'9px 14px',color:'#e65100',marginBottom:'8px',fontSize:'12px'},
  docBox:{background:'#e8f5e9',border:'1px solid #a5d6a7',borderRadius:'8px',padding:'9px 14px',color:'#1b5e20',marginTop:'10px',fontSize:'12px'},
  sampInfo:{display:'flex',gap:'18px',flexWrap:'wrap',background:'#e0f7fa',borderRadius:'8px',padding:'9px 14px',marginBottom:'12px',fontSize:'12px',color:'#00474f'},
  dropdown:{position:'absolute',top:'100%',left:0,right:0,background:'#fff',border:'1px solid #ddd',borderRadius:'8px',maxHeight:'190px',overflowY:'auto',zIndex:50,boxShadow:'0 6px 20px rgba(0,0,0,0.18)'},
  dropItem:{padding:'7px 11px',fontSize:'12px',cursor:'pointer',borderBottom:'1px solid #f5f5f5',textAlign:'right'},
  btnCancel:{padding:'8px 18px',background:'#e0e0e0',color:'#333',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  btnSave:{padding:'8px 22px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  toast:{position:'fixed',bottom:'24px',right:'24px',background:'#1b5e20',color:'#fff',padding:'12px 24px',borderRadius:'10px',fontSize:'14px',fontWeight:'600',zIndex:2000,boxShadow:'0 4px 16px rgba(0,0,0,0.25)'},
};
